
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { legacyApi, Invoice } from '../../../services/legacyApi';
import { CompactTable } from '../../../components/ui/CompactTable';
import { useConfirm } from '../../../hooks/use-confirm';
import { toast } from 'sonner';
import { Download, Plus, Trash2, Pencil, RotateCcw, Eye } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { CreditNoteModal } from '../components/CreditNoteModal';
import { StandardActions } from '../components/StandardActions';
import { TableFilterBar } from '../../../components/table-filters/TableFilterBar';
import { useTableFilterState } from '../../../hooks/useTableFilterState';
import { FlowEntryBanner } from '../../../components/workflow/FlowEntryBanner';
import { useDebouncedFilterValue } from '../../../hooks/useDebouncedFilterValue';

type InvoicePaymentDisplayStatus = 'Pending' | 'Partially Paid' | 'Paid' | 'Credit' | 'Draft' | 'Cancelled';

export const LegacyInvoices: React.FC = () => {
    const PAGE_SIZE = 50;
    const navigate = useNavigate();
    const { confirm } = useConfirm();
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [debouncedQ, setDebouncedQ] = useState('');
    const { state, setFilter, clearAll, isDirty } = useTableFilterState({
        q: '',
        type: 'all',
        payment: 'all',
        dateRange: '|'
    });
    const [searchValue, setSearchValue] = useDebouncedFilterValue(state.q, (value) => setFilter('q', value));
    const [searchParams] = useSearchParams();
    const estimateIdFilter = searchParams.get('estimate_id');
    const createInvoiceUrl = estimateIdFilter
        ? `/legacy/invoices/new?estimate_id=${estimateIdFilter}`
        : '/legacy/invoices/new';

    // Modal State
    const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
    const [selectedInvoiceForReturn, setSelectedInvoiceForReturn] = useState<Invoice | null>(null);

    const formatInvoiceId = (id: number) => `HC/INV/${String(id).padStart(4, '0')}`;
    const formatEstimateRef = (id: number, revision?: number) => {
        if (!id || id <= 0) return 'Direct SO';
        const rev = Number(revision ?? 0);
        const baseRef = `HC/EST/${String(id).padStart(4, '0')}`;
        return rev > 0 ? `${baseRef}/R${rev}` : baseRef;
    };
    const formatAmount = (value?: number) => {
        if (value === undefined || value === null) return '-';
        return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };
    const getDisplayPaymentStatus = (invoice: Invoice): InvoicePaymentDisplayStatus => {
        const statusId = Number(invoice.status_id || 1);
        const grandTotal = Number(invoice.grand_total || 0);

        if (grandTotal < 0) return 'Credit';
        if (statusId === 4) return 'Cancelled';
        if (statusId === 1) return 'Draft';

        const progress = String(invoice.payment_progress || '').trim();
        const paidAmount = Number(invoice.paid_amount || 0);
        const outstandingAmount = Number(invoice.outstanding_amount || 0);

        if (progress === 'Fully Paid' || (grandTotal > 0 && outstandingAmount <= 0)) {
            return 'Paid';
        }
        if (progress === 'Partially Paid' || (paidAmount > 0 && outstandingAmount > 0)) {
            return 'Partially Paid';
        }
        return 'Pending';
    };
    const isDraftTaxInvoice = (invoice: Invoice) =>
        (invoice.invoice_type === 'PROFORMA' ? 'PROFORMA' : 'TAX') === 'TAX' && Number(invoice.status_id || 1) === 1;
    const getInvoiceAgeingDays = (invoice: Invoice): number | null => {
        const paymentStatus = getDisplayPaymentStatus(invoice);
        if (paymentStatus !== 'Pending' && paymentStatus !== 'Partially Paid') {
            return null;
        }
        if (!invoice.invoice_date) return null;

        const parsed = new Date(invoice.invoice_date);
        if (Number.isNaN(parsed.getTime())) return null;

        const invoiceDay = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const diffDays = Math.floor((today.getTime() - invoiceDay.getTime()) / (1000 * 60 * 60 * 24));
        return Math.max(0, diffDays);
    };
    const paymentStatusBadge = (invoice: Invoice) => {
        const paymentStatus = getDisplayPaymentStatus(invoice);
        let cls = 'bg-slate-100 text-slate-700 border-slate-200';
        if (paymentStatus === 'Paid') cls = 'bg-emerald-100 text-emerald-700 border-emerald-200';
        else if (paymentStatus === 'Partially Paid') cls = 'bg-amber-100 text-amber-700 border-amber-200';
        else if (paymentStatus === 'Credit') cls = 'bg-violet-100 text-violet-700 border-violet-200';
        else if (paymentStatus === 'Draft') cls = 'bg-blue-100 text-blue-700 border-blue-200';
        else if (paymentStatus === 'Cancelled') cls = 'bg-red-100 text-red-700 border-red-200';
        return <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`}>{paymentStatus}</span>;
    };
    const typeBadge = (invoice: Invoice) => {
        const token = invoice.invoice_type === 'PROFORMA' ? 'PROFORMA' : 'TAX';
        const cls = token === 'PROFORMA'
            ? 'bg-amber-100 text-amber-700 border-amber-200'
            : 'bg-emerald-100 text-emerald-700 border-emerald-200';
        return <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`}>{token}</span>;
    };

    useEffect(() => {
        const t = window.setTimeout(() => setDebouncedQ(state.q.trim()), 250);
        return () => window.clearTimeout(t);
    }, [state.q]);

    const loadInvoices = async (reset: boolean = true) => {
        const nextOffset = reset ? 0 : invoices.length;
        if (reset) setLoading(true);
        else setLoadingMore(true);
        const [start, end] = state.dateRange.split('|');
        try {
            const response = await legacyApi.sales.listInvoicesPaged({
                estimate_id: estimateIdFilter ? Number(estimateIdFilter) : undefined,
                q: debouncedQ || undefined,
                invoice_type: state.type !== 'all' ? state.type : undefined,
                payment_status: state.payment !== 'all' ? state.payment : undefined,
                date_from: start || undefined,
                date_to: end || undefined,
                limit: PAGE_SIZE,
                offset: nextOffset,
            });
            setInvoices((prev) => (reset ? response.items : [...prev, ...response.items]));
            setTotalCount(response.total);
            setHasMore(response.has_more);
        } catch (err) {
            console.error(err);
        } finally {
            if (reset) {
                setLoading(false);
                setHasLoadedOnce(true);
            }
            else setLoadingMore(false);
        }
    };

    useEffect(() => {
        loadInvoices(true);
    }, [estimateIdFilter, debouncedQ, state.type, state.payment, state.dateRange]);

    const handleDelete = async (id: number) => {
        const confirmed = await confirm(`Delete ${formatInvoiceId(id)}?`, {
            description: 'This will also void the associated Journal Entry. This action cannot be undone.',
            confirmText: 'Delete Invoice',
            variant: 'destructive'
        });

        if (!confirmed) return;

        try {
            await legacyApi.sales.deleteInvoice(id);
            setInvoices(prev => prev.filter(i => i.invoice_id !== id));
            setTotalCount(prev => Math.max(prev - 1, 0));
            toast.success("Invoice deleted");
        } catch (error: any) {
            toast.error(error.message || 'Failed to delete invoice');
        }
    };

    const handleReturnClick = (invoice: Invoice) => {
        setSelectedInvoiceForReturn(invoice);
        setIsReturnModalOpen(true);
    };

    if (!hasLoadedOnce && loading) return <div className="p-4">Loading Invoices...</div>;

    const columns = [
        { header: "ID", accessor: (i: Invoice) => formatInvoiceId(i.invoice_id), className: 'font-mono whitespace-nowrap w-32' },
        {
            header: "Estimate",
            accessor: (i: Invoice) => {
                if (!i.estimate_id || i.estimate_id <= 0) {
                    return i.so_id ? (
                        <Link
                            to={`/legacy/orders/${i.so_id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="font-mono text-blue-600 hover:underline"
                        >
                            {`Direct SO HC/SO/${String(i.so_id).padStart(4, '0')}`}
                        </Link>
                    ) : (
                        <span className="text-muted-foreground">Direct SO</span>
                    );
                }
                return (
                    <Link
                        to={`/legacy/estimates/${i.estimate_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="font-mono text-blue-600 hover:underline"
                    >
                        {i.estimate_ref || formatEstimateRef(i.estimate_id, i.estimate_revision_no)}
                    </Link>
                );
            },
            className: 'whitespace-nowrap w-32'
        },
        {
            header: "Source",
            accessor: (i: Invoice) => {
                if (i.source_summary) {
                    return <span className="text-xs text-slate-700">{i.source_summary}</span>;
                }
                if (i.so_id) {
                    return <span className="text-xs text-slate-700">{`SO HC/SO/${String(i.so_id).padStart(4, '0')}`}</span>;
                }
                return <span className="text-muted-foreground text-xs">-</span>;
            },
            className: 'min-w-[180px] max-w-[260px] truncate'
        },
        { header: "Customer", accessor: (i: Invoice) => i.customer_name || '-', className: 'min-w-[200px] max-w-[300px] truncate' },
        { header: "Invoice Date", accessor: (i: Invoice) => i.invoice_date ? new Date(i.invoice_date).toLocaleDateString() : '-', className: 'w-24 whitespace-nowrap' },
        { header: "Type", accessor: (i: Invoice) => typeBadge(i), className: 'w-28' },
        { header: "Customer PO#", accessor: (i: Invoice) => i.customer_po_no || '-', className: 'min-w-[140px] max-w-[220px] truncate' },
        { header: "Payment Status", accessor: (i: Invoice) => paymentStatusBadge(i), className: 'w-28' },
        {
            header: "Ageing (Days)",
            accessor: (i: Invoice) => {
                const ageingDays = getInvoiceAgeingDays(i);
                return ageingDays === null ? '-' : ageingDays;
            },
            className: 'w-20 text-right font-mono tabular-nums'
        },
        { header: "Invoice Amount", accessor: (i: Invoice) => formatAmount(i.grand_total), className: 'w-32 text-right font-medium tabular-nums' },
        {
            header: "Actions",
            accessor: (i: Invoice) => (
                <StandardActions
                    view={{
                        title: "View Invoice",
                        onClick: (e) => {
                            e.stopPropagation();
                            navigate(`/legacy/invoices/${i.invoice_id}`);
                        },
                        icon: <Eye className="w-4 h-4 text-green-600" />
                    }}
                    edit={{
                        title: "Edit Invoice",
                        onClick: (e) => {
                            e.stopPropagation();
                            navigate(`/legacy/invoices/edit/${i.invoice_id}`);
                        },
                        icon: <Pencil className="w-4 h-4 text-blue-600" />
                    }}
                    download={{
                        title: isDraftTaxInvoice(i) ? "Download Draft Preview" : "Download PDF",
                        onClick: async (e) => {
                            e.stopPropagation();
                            await legacyApi.sales.downloadInvoicePDF(i.invoice_id);
                        },
                        icon: <Download className="w-4 h-4 text-purple-600" />
                    }}
                    remove={{
                        title: "Delete Invoice",
                        onClick: (e) => {
                            e.stopPropagation();
                            handleDelete(i.invoice_id);
                        },
                        icon: <Trash2 className="w-4 h-4 text-red-600" />
                    }}
                    overflow={[
                        {
                            label: "Create Credit Note",
                            onClick: (e) => {
                                e.stopPropagation();
                                handleReturnClick(i);
                            },
                            icon: <RotateCcw className="w-4 h-4 text-orange-600" />
                        }
                    ]}
                />
            ),
            className: 'w-32' // Adjusted for extra button
        }
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-end">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-foreground">Invoices</h1>
                    <p className="text-muted-foreground">Manage invoices and revenue - showing {invoices.length} of {totalCount}</p>
                </div>
                <Link to={createInvoiceUrl}>
                    <Button className="bg-primary text-primary-foreground">
                        <Plus className="mr-2 h-4 w-4" /> New Invoice
                    </Button>
                </Link>
            </div>

            <div className="space-y-4">
                <FlowEntryBanner
                    title="Invoice Path Guidance"
                    pathLabel="Delivery -> Invoice"
                    nextHint="Invoice quantity should follow delivered-not-invoiced balance."
                    helpModule="finance"
                />
                <div className="flex items-center gap-2">
                    <TableFilterBar
                        searchValue={searchValue}
                        onSearchChange={setSearchValue}
                        searchPlaceholder="Search invoices, source, customer, PO..."
                        controls={[
                            {
                                key: 'dateRange',
                                label: 'Date',
                                type: 'daterange',
                                value: state.dateRange,
                                onChange: (value) => setFilter('dateRange', value)
                            },
                            {
                                key: 'type',
                                label: 'Type',
                                type: 'select',
                                value: state.type,
                                onChange: (value) => setFilter('type', value),
                                options: [
                                    { value: 'all', label: 'All Types' },
                                    { value: 'TAX', label: 'Tax' },
                                    { value: 'PROFORMA', label: 'Proforma' }
                                ]
                            },
                            {
                                key: 'payment',
                                label: 'Payment Status',
                                type: 'select',
                                value: state.payment,
                                onChange: (value) => setFilter('payment', value),
                                options: [
                                    { value: 'all', label: 'All Payment Status' },
                                    { value: 'pending', label: 'Pending' },
                                    { value: 'partially_paid', label: 'Partially Paid' },
                                    { value: 'paid', label: 'Paid' },
                                    { value: 'credit', label: 'Credit Note' }
                                ]
                            }
                        ]}
                        isDirty={isDirty}
                        onClear={clearAll}
                    />
                </div>

                <div className="flex-1 overflow-auto border rounded-md bg-white shadow-sm">
                    <>
                        <CompactTable
                            data={invoices}
                            columns={columns}
                            keyField="invoice_id"
                            loading={loading}
                            onRowClick={(i) => navigate(`/legacy/invoices/${i.invoice_id}`)}
                        />
                        {hasMore && (
                            <div className="flex justify-center py-4">
                                <Button variant="ghost" onClick={() => loadInvoices(false)} disabled={loadingMore}>
                                    {loadingMore ? 'Loading...' : 'Load older invoices'}
                                </Button>
                            </div>
                        )}
                    </>
                </div>
            </div>

            {selectedInvoiceForReturn && (
                <CreditNoteModal
                    invoice={selectedInvoiceForReturn}
                    isOpen={isReturnModalOpen}
                    onClose={() => {
                        setIsReturnModalOpen(false);
                        setSelectedInvoiceForReturn(null);
                    }}
                    onSuccess={() => {
                        toast.success("Return processed successfully");
                        // Refresh logic could go here if needed
                    }}
                />
            )}
        </div>
    );
};
